import { useEffect, useState } from "react";
import axios from "axios";
import { Quote, Star, Clock3, BadgeCheck, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal, SectionHeading } from "./Reveal";
import { ReviewForm } from "./ReviewForm";
import { REAL_TESTIMONIALS, TESTIMONIAL_SLOTS, API } from "@/lib/site";

const Stars = ({ filled }) => (
  <div className="flex gap-0.5" aria-label={`${filled} star rating`}>
    {Array.from({ length: 5 }).map((_, s) => (
      <Star
        key={s}
        className={`h-4 w-4 ${s < filled ? "fill-[#D97706] stroke-[#D97706]" : "fill-none stroke-[#D97706]/40"}`}
      />
    ))}
  </div>
);

export const Testimonials = () => {
  const [viewer, setViewer] = useState(null);
  const [uploaded, setUploaded] = useState([]);
  const [visitorReviews, setVisitorReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/media`)
      .then(({ data }) => setUploaded(data))
      .catch(() => {});
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => setVisitorReviews(data))
      .catch(() => {});
  }, []);

  const dynamicReviews = uploaded
    .filter((m) => m.kind.startsWith("review"))
    .map((m) => ({
      id: m.id,
      name: m.reviewer || "NAVYOGA Student",
      quote: m.label || "",
      isVideo: m.kind.endsWith("video"),
      src: `${API}/media/file/${m.id}`,
    }));

  return (
    <section id="stories" data-testid="testimonials-placeholder-section" className="bg-[#F8FFF8] py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading
          testId="testimonials-heading"
          overline="Testimonials"
          title="What Our Community Says"
          description="Real words from real students — shared with love on WhatsApp, published with permission. More verified stories from our members are on their way."
        />

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {REAL_TESTIMONIALS.map((t, i) => (
            <Reveal key={t.id} delay={0.06 * (i % 3)}>
              <div
                data-testid={`testimonial-real-${t.id}`}
                className="flex h-full flex-col rounded-3xl border border-emerald-900/10 bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(30,58,138,0.25)]"
              >
                <div className="flex items-center justify-between">
                  <Quote className="h-7 w-7 text-[#2E8B57]/40" />
                  <Stars filled={t.stars} />
                </div>
                <p className="mt-5 text-sm italic leading-relaxed text-slate-700 sm:text-base">“{t.quote}”</p>
                <button
                  data-testid={`testimonial-image-button-${t.id}`}
                  onClick={() => setViewer({ src: t.image, name: t.name, isVideo: false })}
                  className="group relative mt-5 block overflow-hidden rounded-2xl border border-emerald-900/10"
                  aria-label={`View full review from ${t.name}`}
                >
                  <img
                    src={t.image}
                    alt={`WhatsApp review from ${t.name}`}
                    loading="lazy"
                    className="aspect-[9/12] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-slate-900/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                    <Maximize2 className="h-3 w-3" />
                    View full
                  </span>
                </button>
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.tag}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2E8B57]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#2E8B57]">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Verified review
                  </span>
                </div>
              </div>
            </Reveal>
          ))}

          {dynamicReviews.map((r, i) => (
            <Reveal key={r.id} delay={0.06 * (i % 3)}>
              <div
                data-testid={`testimonial-uploaded-${r.id}`}
                className="flex h-full flex-col rounded-3xl border border-emerald-900/10 bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(30,58,138,0.25)]"
              >
                <div className="flex items-center justify-between">
                  <Quote className="h-7 w-7 text-[#2E8B57]/40" />
                  <Stars filled={5} />
                </div>
                {r.quote && <p className="mt-5 text-sm italic leading-relaxed text-slate-700 sm:text-base">“{r.quote}”</p>}
                {r.isVideo ? (
                  <video
                    data-testid={`testimonial-video-${r.id}`}
                    src={r.src}
                    controls
                    playsInline
                    preload="metadata"
                    className="mt-5 w-full rounded-2xl border border-emerald-900/10"
                  />
                ) : (
                  <button
                    data-testid={`testimonial-image-button-${r.id}`}
                    onClick={() => setViewer({ src: r.src, name: r.name, isVideo: false })}
                    className="group relative mt-5 block overflow-hidden rounded-2xl border border-emerald-900/10"
                    aria-label={`View full review from ${r.name}`}
                  >
                    <img
                      src={r.src}
                      alt={`Review from ${r.name}`}
                      loading="lazy"
                      className="aspect-[9/12] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                    <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-slate-900/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                      <Maximize2 className="h-3 w-3" />
                      View full
                    </span>
                  </button>
                )}
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.isVideo ? "Video Review" : "Yoga Student"}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2E8B57]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#2E8B57]">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Verified review
                  </span>
                </div>
              </div>
            </Reveal>
          ))}

          {visitorReviews.map((r, i) => (
            <Reveal key={r.id} delay={0.06 * (i % 3)}>
              <div
                data-testid={`testimonial-visitor-${r.id}`}
                className="flex h-full flex-col rounded-3xl border border-emerald-900/10 bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(30,58,138,0.25)]"
              >
                <div className="flex items-center justify-between">
                  <Quote className="h-7 w-7 text-[#2E8B57]/40" />
                  <Stars filled={r.rating} />
                </div>
                <p className="mt-5 text-sm italic leading-relaxed text-slate-700 sm:text-base">“{r.text}”</p>
                {r.has_photo && (
                  <button
                    data-testid={`testimonial-image-button-${r.id}`}
                    onClick={() => setViewer({ src: `${API}/reviews/photo/${r.id}`, name: r.name, isVideo: false })}
                    className="group relative mt-5 block overflow-hidden rounded-2xl border border-emerald-900/10"
                    aria-label={`View photo from ${r.name}`}
                  >
                    <img
                      src={`${API}/reviews/photo/${r.id}`}
                      alt={`Shared by ${r.name}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                    <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-slate-900/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                      <Maximize2 className="h-3 w-3" />
                      View full
                    </span>
                  </button>
                )}
                <div className="mt-auto flex items-center justify-between pt-5">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(r.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2E8B57]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#2E8B57]">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Approved review
                  </span>
                </div>
              </div>
            </Reveal>
          ))}

          {TESTIMONIAL_SLOTS.map((slot, i) => (
            <Reveal key={slot.id} delay={0.06 * (i % 3)}>
              <div
                data-testid={`testimonial-slot-${slot.id}`}
                className="flex h-full flex-col rounded-3xl border border-emerald-900/10 bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(30,58,138,0.25)]"
              >
                <div className="flex items-center justify-between">
                  <Quote className="h-7 w-7 text-[#2E8B57]/30" />
                  <Stars filled={0} />
                </div>
                <div className="mt-6 flex flex-1 items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-[#F8FFF8] px-6 py-10">
                  <p className="text-center text-sm italic leading-relaxed text-slate-400">
                    “A verified story from our community will appear here soon.”
                  </p>
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <span className="rounded-full bg-[#1E3A8A]/5 px-3.5 py-1.5 text-xs font-semibold text-[#1E3A8A]">{slot.tag}</span>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                    <Clock3 className="h-3 w-3" />
                    Reserved slot
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <ReviewForm />
      </div>

      <Dialog open={!!viewer} onOpenChange={() => setViewer(null)}>
        <DialogContent data-testid="testimonial-viewer-modal" className="max-w-md rounded-3xl border-emerald-900/10 bg-white p-4">
          {viewer && (
            <>
              <DialogTitle className="sr-only">Review from {viewer.name}</DialogTitle>
              <img src={viewer.src} alt={`Full review from ${viewer.name}`} className="max-h-[75vh] w-full rounded-2xl object-contain" />
              <p className="px-2 pb-2 pt-3 text-center text-sm font-semibold text-slate-800">{viewer.name}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
